import React, { useState, useEffect } from 'react';
import {
  Page,
  Layout,
  Card,
  Button,
  Banner,
  Text,
  Spinner
} from '@shopify/polaris';
import Toast from '../components/Toast';
import { api, apiHelpers } from '../utils/api';
import { startSubscription, formatPlanPrice } from '../utils/billing';

// Current subscription. There is one plan, so nothing to change to; uninstalling
// the app cancels it.
function PlanCard() {
  const [billing, setBilling] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    apiHelpers.getBillingStatus()
      .then((res) => setBilling(res.data))
      .catch(() => setFailed(true));
  }, []);

  const plan = billing && billing.plan;
  const price = formatPlanPrice(plan);
  const renews = plan && plan.currentPeriodEnd
    ? new Date(plan.currentPeriodEnd).toLocaleDateString()
    : null;

  return (
    <Card>
      <div style={{ padding: '16px' }}>
        <Text variant="headingMd" as="h3">Plan</Text>
        <div style={{ marginTop: '12px' }}>
          {!billing && !failed && <Spinner size="small" />}
          {failed && <Text as="p" color="subdued">Couldn't load your plan right now.</Text>}
          {billing && (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
              <div>
                <Text as="p" variant="bodyMd" fontWeight="medium">
                  {plan ? `${plan.name}${price ? ` · ${price}` : ''}` : 'No active plan'}
                  {plan && plan.test ? ' (test)' : ''}
                </Text>
                {plan && <br />}
                {plan && (
                  <Text as="p" color="subdued">
                    {renews ? `Renews ${renews}. ` : ''}Billed through Shopify. Uninstalling Order Guard cancels the plan.
                  </Text>
                )}
              </div>
              {!plan && <Button primary onClick={() => startSubscription().catch(() => setFailed(true))}>Start plan</Button>}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

// Progress and problems behind "Apply limits per delivery address".
function AddressStatus({ status }) {
  if (!status || status.state === 'off') return null;
  return (
    <div style={{ marginTop: '8px' }}>
      {status.state === 'preparing' && (
        <Text as="p" color="subdued">
          Reading your past orders. Checkout starts checking addresses when this finishes.
        </Text>
      )}
      {status.state === 'failed' && (
        <Banner status="critical">
          <p>{status.failure || "Order Guard couldn't turn this on."}</p>
        </Banner>
      )}
      {status.state === 'on' && status.overflow.length > 0 && (
        <Banner status="warning" title="Too many addresses to check at checkout">
          <p>
            These products are limited per account only until they fit again:{' '}
            {status.overflow.map((product) => product.title).join(', ')}.
          </p>
        </Banner>
      )}
    </div>
  );
}

function Settings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [toastActive, setToastActive] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const [settings, setSettings] = useState({
    enableGlobalLimits: true,
    limitByAddress: false
  });
  const [addressStatus, setAddressStatus] = useState(null);

  // While past orders are being read, check back until the address check is live.
  useEffect(() => {
    if (addressStatus?.state !== 'preparing') return undefined;
    const timer = setInterval(() => {
      api.get('/settings')
        .then((response) => setAddressStatus(response.data.addressLimits || null))
        .catch(() => {});
    }, 10000);
    return () => clearInterval(timer);
  }, [addressStatus?.state]);

  // Load settings once on mount.
  useEffect(() => {
    loadSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError(null);

      // In a real implementation, you'd have an endpoint to get app settings
      // For now, we'll use default settings
      const response = await api.get('/settings').catch(() => ({ data: { settings } }));

      if (response.data.settings) {
        setSettings(response.data.settings);
      }
      setAddressStatus(response.data.addressLimits || null);
    } catch (err) {
      console.error('Load settings error:', err);
      setError('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      const response = await api.post('/settings', { settings });
      // A save doesn't re-check address access, so keep what the page loaded with.
      setAddressStatus((previous) => ({ ...(response.data.addressLimits || {}), available: previous?.available }));

      showToast('Settings saved successfully');
    } catch (err) {
      console.error('Save settings error:', err);
      setError('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSettingChange = (key, value) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const showToast = (message) => {
    setToastMessage(message);
    setToastActive(true);
  };


  if (loading) {
    return (
      <Page title="Settings">
        <Layout>
          <Layout.Section>
            <Card>
              <div style={{ padding: '40px', textAlign: 'center' }}>
                <Spinner size="large" />
              </div>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <Page
      title="Settings"
      primaryAction={{
        content: 'Save Settings',
        onAction: handleSave,
        loading: saving
      }}
    >
      <Layout>
        {error && (
          <Layout.Section>
            <Banner status="critical" title="Error">
              <p>{error}</p>
              <Button onClick={loadSettings}>Retry</Button>
            </Banner>
          </Layout.Section>
        )}

        <Layout.Section>
          <PlanCard />
        </Layout.Section>

        {/* General Settings */}
        <Layout.Section>
          <Card>
            <div style={{ padding: '16px' }}>
              <Text variant="headingMd" as="h3">
                General Settings
              </Text>
              <div style={{ marginTop: '16px', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <button
                  type="button"
                  role="switch"
                  aria-checked={settings.enableGlobalLimits}
                  aria-label="Enable purchase limits"
                  className={`og-switch${settings.enableGlobalLimits ? ' og-switch--on' : ''}`}
                  onClick={() => handleSettingChange('enableGlobalLimits', !settings.enableGlobalLimits)}
                >
                  <span className="og-switch__knob" />
                </button>
                <div>
                  <Text as="p" variant="bodyMd" fontWeight="medium">Enable purchase limits</Text>
                  <Text as="p" color="subdued">When disabled, all purchase limits will be ignored</Text>
                </div>
              </div>
              {/* Shown only once Shopify lets Order Guard read delivery addresses, or
                  while it's on, so it can still be switched off. */}
              {(addressStatus?.available || settings.limitByAddress) && (
              <div style={{ marginTop: '16px', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                <button
                  type="button"
                  role="switch"
                  aria-checked={Boolean(settings.limitByAddress)}
                  aria-label="Apply limits per delivery address"
                  className={`og-switch${settings.limitByAddress ? ' og-switch--on' : ''}`}
                  onClick={() => handleSettingChange('limitByAddress', !settings.limitByAddress)}
                >
                  <span className="og-switch__knob" />
                </button>
                <div>
                  <Text as="p" variant="bodyMd" fontWeight="medium">Apply limits per delivery address</Text>
                  <Text as="p" color="subdued">
                    Orders to the same address count towards one shared limit, even from different accounts. Collection orders are still limited per account.
                  </Text>
                  <AddressStatus status={addressStatus} />
                </div>
              </div>
              )}
            </div>
          </Card>
        </Layout.Section>
      </Layout>

      {toastActive && (
        <Toast
          content={toastMessage}
          onDismiss={() => setToastActive(false)}
        />
      )}
    </Page>
  );
}

export default Settings;
